import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useCart } from '../contexts/CartContext';
import ColorSwatch from '../components/ColorSwatch';
import Footer from '../components/Footer';
import './ProductDetail.css';

const productDetails = {
  classic: {
    title: 'Classic NeNe',
    price: '$22',
    description: 'Solid color press-ons in your favorite shade. Simple, sleek, and timeless.',
    fields: ['Color', 'Shape', 'Length', 'Sizes'],
  },
  frenchie: {
    title: 'Frenchie & Friends',
    price: '$33',
    description: 'French tips, 1 3D element, or a full chrome set. Intermediate but bold.',
    fields: ['Color', 'Shape', 'Length', 'Sizes', 'Add-Ons', 'Inspo Image', 'Custom Notes'],
  },
  museum: {
    title: 'The Museum',
    price: '$44',
    description: 'Statement sets with layered gel, chrome, & designs. For main character energy.',
    fields: ['Color', 'Shape', 'Length', 'Sizes', 'Add-Ons', 'Inspo Image', 'Custom Notes'],
  },
  custom: {
    title: 'Custom Set',
    price: '$55+',
    description: 'Let’s build from scratch — inspired by your vision, occasion, or mood.',
    fields: ['Color', 'Shape', 'Length', 'Sizes', 'Add-Ons', 'Inspo Image', 'Custom Notes'],
    isCustom: true,
  },
  signature: {
    title: "NeNe Nail’d It Again",
    price: '$66',
    description: 'Signature recreatable sets — iconic, art-heavy designs recreated exactly as shown. Choose your shape and length.',
    fields: ['Shape', 'Length', 'Sizes'], // no Color / Add-Ons / Inspo
    isSignature: true,
  },
};

// cents for totals / Stripe later (keep display label too)
const priceCentsMap = {
  classic: 2200,
  frenchie: 3300,
  museum: 4400,
  custom: null,     // set dynamically if needed
  signature: 6600,
};

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  // Declare hooks FIRST (no conditional returns before this point)
  const [selectedShape, setSelectedShape] = useState('');
  const [selectedLength, setSelectedLength] = useState('');
  const [errors, setErrors] = useState({});
  const [formState, setFormState] = useState({
    color: '',               // { name, hex } when selected
    sizeType: '',            // "Small" | "Medium" | "Large" | "Custom"
    customMode: 'entry',     // "entry" | "fitting" (only for Custom)
    measurements: {
      L: ['', '', '', '', ''], // Thumb->Pinky (Left)
      R: ['', '', '', '', ''], // Thumb->Pinky (Right)
    },
    addons: '',
    inspo: null,
    notes: '',
    fulfillment: 'shipping', // 'shipping' | 'in_person'
  });

  // Safe lookup (don’t conditionally call hooks). Provide a harmless fallback object.
  const rawData = productDetails[slug];
  const data = rawData || {
    title: 'Product',
    price: '',
    description: '',
    fields: [],
    isSignature: false,
    isCustom: false,
  };

  const requires = new Set(data.fields);
  const backTo =
    slug === 'custom'
      ? '/nails'
      : ['classic', 'frenchie', 'museum', 'signature'].includes(slug)
      ? '/nails/signature'
      : '/nails';

  // helpers
  const allFilled = (arr) => arr.every((v) => v?.toString().trim() !== '');
  const validateMeasurements = (m) => allFilled(m.L) && allFilled(m.R);

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};

    if (requires.has('Color') && !formState.color) newErrors.color = 'Please select a color.';
    if (requires.has('Shape') && !selectedShape) newErrors.shape = 'Please select a shape.';
    if (requires.has('Length') && !selectedLength) newErrors.length = 'Please select a length.';

    if (requires.has('Sizes') && !formState.sizeType) {
      newErrors.sizes = 'Please choose a size.';
    } else if (formState.sizeType === 'Custom') {
      if (formState.customMode === 'entry' && !validateMeasurements(formState.measurements)) {
        newErrors.sizes = 'Please enter all 10 measurements (left & right hand).';
      }
    }

    if (requires.has('Add-Ons') && formState.addons.length > 120) {
      newErrors.addons = 'Keep add-ons under 120 characters.';
    }
    if (requires.has('Custom Notes') && formState.notes.length > 300) {
      newErrors.notes = 'Keep notes under 300 characters.';
    }

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    // stable key so cart can merge identical selections
    const variantKey = [
      slug,
      requires.has('Color') ? (formState.color?.name || 'no-color') : 'no-color-field',
      requires.has('Shape') ? (selectedShape || 'no-shape') : 'no-shape-field',
      requires.has('Length') ? (selectedLength || 'no-length') : 'no-length-field',
      requires.has('Sizes') ? (formState.sizeType || 'no-size') : 'no-size-field',
      formState.customMode || 'no-mode',
      formState.fulfillment || 'shipping',
    ].join('|');

    const product = {
      slug,
      title: data.title,
      price: data.price,                    // display label
      priceCents: priceCentsMap[slug],      // integer cents (or null for custom)
      isSignature: !!data.isSignature,
      isCustom: !!data.isCustom,
      color: formState.color,               // { name, hex } or ''
      shape: selectedShape,
      length: selectedLength,
      sizes: {
        sizeType: formState.sizeType,
        customMode: formState.customMode,
        measurements: formState.sizeType === 'Custom' ? formState.measurements : null,
      },
      addons: formState.addons,
      inspo: formState.inspo,               // File or null (upload later)
      notes: formState.notes,
      fulfillment: formState.fulfillment,   // shipping vs in_person
      quantity: 1,
      variantKey,
    };

    addToCart(product);
    navigate('/nails/cart');
  };

  const renderField = (field, idx) => {
    // Signature: skip color (we still render other fields)
    if (data.isSignature && field === 'Color') return null;

    switch (field) {
      case 'Color':
        return (
          <div className="form-group color-group" key={`color-${idx}`}>
            <label>
              Color <span className="text-red-500">*</span>
            </label>
            <ColorSwatch
              onSelect={({ name, hex }) => {
                setFormState((prev) => ({ ...prev, color: { name, hex } }));
                setErrors((e) => ({ ...e, color: undefined }));
              }}
            />
            {formState.color?.name && (
              <p className="selected-color-note">
                Selected Color: <strong>{formState.color.name}</strong>
              </p>
            )}
            {errors.color && <p className="text-red-600 text-sm mt-1">{errors.color}</p>}
          </div>
        );

      case 'Shape':
        return (
          <div className="form-group" key={`shape-${idx}`}>
            <label>
              Shape <span className="text-red-500">*</span>
            </label>
            <div className="shape-options">
              {['Square', 'Almondetto', 'Coffin'].map((shape) => (
                <button
                  key={shape}
                  type="button"
                  className={`shape-btn ${selectedShape === shape ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedShape(shape);
                    setErrors((e) => ({ ...e, shape: undefined }));
                  }}
                >
                  {shape}
                </button>
              ))}
            </div>
            {errors.shape && <p className="text-red-600 text-sm mt-1">{errors.shape}</p>}
          </div>
        );

      case 'Length':
        return (
          <div className="form-group" key={`length-${idx}`}>
            <label>
              Length <span className="text-red-500">*</span>
            </label>
            <div className="length-options">
              {['Short', 'Medium', 'Long'].map((length) => (
                <button
                  key={length}
                  type="button"
                  className={`length-btn ${selectedLength === length ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedLength(length);
                    setErrors((e) => ({ ...e, length: undefined }));
                  }}
                >
                  {length}
                </button>
              ))}
            </div>
            {errors.length && <p className="text-red-600 text-sm mt-1">{errors.length}</p>}
          </div>
        );

      case 'Sizes':
        return (
          <div className="form-group" key={`sizes-${idx}`}>
            <label>
              Sizes <span className="text-red-500">*</span>
            </label>

            <select
              name="sizeType"
              value={formState.sizeType}
              onChange={(e) => {
                const v = e.target.value;
                setFormState((prev) => ({ ...prev, sizeType: v }));
                setErrors((er) => ({ ...er, sizes: undefined }));
              }}
              className="border border-gray-300 rounded-md p-2 w-full"
            >
              <option value="">-- Select a size --</option>
              <option value="Small">Small</option>
              <option value="Medium">Medium</option>
              <option value="Large">Large</option>
              <option value="Custom">Custom</option>
            </select>

            {formState.sizeType === 'Custom' && (
              <div className="mt-3 border rounded-md p-3 space-y-3">
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={formState.customMode === 'entry'}
                      onChange={() =>
                        setFormState((prev) => ({ ...prev, customMode: 'entry' }))
                      }
                    />
                    Enter my measurements
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={formState.customMode === 'fitting'}
                      onChange={() =>
                        setFormState((prev) => ({ ...prev, customMode: 'fitting' }))
                      }
                    />
                    In-person fitting (Atlanta/popup)
                  </label>
                </div>

                {formState.customMode === 'entry' ? (
                  <>
                    <p className="text-sm text-gray-600">
                      Choose a size for each finger and record. Left & Right hands.
                    </p>

                    <div className="grid grid-cols-2 gap-4">
                      {['L', 'R'].map((hand) => (
                        <div key={hand}>
                          <div className="text-xs font-medium mb-2">
                            {hand === 'L' ? 'Left' : 'Right'} hand — Thumb → Pinky
                          </div>
                          <div className="grid grid-cols-5 gap-2">
                            {['T', 'I', 'M', 'R', 'P'].map((_, i) => (
                              <input
                                key={`${hand}-${i}`}
                                type="number"
                                min={8}
                                max={25}
                                placeholder={['Thumb', 'Index', 'Middle', 'Ring', 'Pinky'][i]}
                                className="border rounded p-2 w-full"
                                value={formState.measurements[hand][i]}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setFormState((prev) => {
                                    const copy = {
                                      ...prev.measurements,
                                      [hand]: [...prev.measurements[hand]],
                                    };
                                    copy[hand][i] = v;
                                    return { ...prev, measurements: copy };
                                  });
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-600">
                    We’ll measure you at your appointment or pop-up. You’ll pick a time on the next step.
                  </p>
                )}
              </div>
            )}

            {errors.sizes && (
              <p className="text-red-600 text-sm mt-2">{errors.sizes}</p>
            )}
          </div>
        );

      case 'Add-Ons':
        return (
          <div className="form-group" key={`addons-${idx}`}>
            <label>Add-Ons (Optional)</label>
            <input
              name="addons"
              placeholder="Chrome, gems, 3D, etc."
              value={formState.addons}
              onChange={(e) => {
                setFormState((prev) => ({ ...prev, addons: e.target.value }));
                setErrors((er) => ({ ...er, addons: undefined }));
              }}
            />
            {errors.addons && <p className="text-red-600 text-sm mt-1">{errors.addons}</p>}
          </div>
        );

      case 'Inspo Image':
        return (
          <div className="form-group" key={`inspo-${idx}`}>
            <label>Inspo Image</label>
            <input
              type="file"
              name="inspo"
              accept="image/*"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f && f.size > 5 * 1024 * 1024) {
                  setErrors((prev) => ({ ...prev, inspo: 'Max file size is 5MB.' }));
                  return;
                }
                setFormState((prev) => ({ ...prev, inspo: f }));
                setErrors((prev) => ({ ...prev, inspo: undefined }));
              }}
            />
            {errors.inspo && <p className="text-red-600 text-sm mt-1">{errors.inspo}</p>}
          </div>
        );

      case 'Custom Notes':
        return (
          <div className="form-group" key={`notes-${idx}`}>
            <label>Custom Notes</label>
            <textarea
              name="notes"
              placeholder="Tell me what you’re envisioning..."
              value={formState.notes}
              onChange={(e) => {
                setFormState((prev) => ({ ...prev, notes: e.target.value }));
                setErrors((er) => ({ ...er, notes: undefined }));
              }}
            />
            {errors.notes && <p className="text-red-600 text-sm mt-1">{errors.notes}</p>}
          </div>
        );

      default:
        return null;
    }
  };

  const showColorPreview = requires.has('Color') && formState.color;

  return (
    <>
      <Helmet>
        <title>{data.title} | NeNeNail’dIt</title>
        <meta
          name="description"
          content={`Customize your ${data.title} set. ${data.description}`}
        />
        <meta
          name="keywords"
          content={`custom nails, ${data.title.toLowerCase()}, press-on nails, handmade nails`}
        />
        <meta property="og:title" content={`${data.title} | NeNeNail’dIt`} />
        <meta
          property="og:description"
          content={
            data.isSignature
              ? `Recreated exactly as shown — choose your shape & length for ${data.title}.`
              : `Create your ${data.title} set with NeNe. Choose your shape, length, color, and more.`
          }
        />
        <meta property="og:url" content={`https://nenethearchitect.com/nails/${slug}`} />
        <meta property="og:image" content="https://nenethearchitect.com/preview.png" />
      </Helmet>

      {/* Not-found UI without conditional hooks: just render conditionally in JSX */}
      {!rawData ? (
        <div className="px-4 py-10 max-w-5xl mx-auto">
          <h1 className="text-2xl font-bold mt-4">Product not found.</h1>
          <Link to="/nails" className="underline text-[#e86ba5]">Back to shop</Link>
          <Footer />
        </div>
      ) : (
        <>
          <div className="product-detail-wrapper">
            <Link to={backTo} className="back-button">← Back</Link>

            <div className="product-detail-page">
              <h1>{data.title}</h1>
              <p className="price">{data.price}</p>
              <p className="description">{data.description}</p>

              {data.isSignature && (
                <p className="text-sm italic text-gray-600 mb-4">
                  This design will be recreated exactly as shown. Only shape, length, and fit can be customized.
                </p>
              )}

              <form className="product-form" onSubmit={handleSubmit}>
                {data.fields.map((field, idx) => renderField(field, idx))}

                {/* Fulfillment toggle
                <div className="form-group">
                  <label>Fulfillment <span className="text-red-500">*</span></label>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="fulfillment"
                        value="shipping"
                        checked={formState.fulfillment === 'shipping'}
                        onChange={() => setFormState((p) => ({ ...p, fulfillment: 'shipping' }))}
                      />
                      Ship to me (flat rate)
                    </label>
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="fulfillment"
                        value="in_person"
                        checked={formState.fulfillment === 'in_person'}
                        onChange={() => setFormState((p) => ({ ...p, fulfillment: 'in_person' }))}
                      />
                      In-person pickup / install (no shipping fee)
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    In-person typically available in Atlanta & pop-ups. Details shown at checkout/confirmation.
                  </p>
                </div> */}

                {(showColorPreview || selectedShape || selectedLength) && (
                  <div className="preview-box">
                    <h4>Your Choices:</h4>
                    <ul>
                      {showColorPreview && (
                        <li>
                          <strong>Color:</strong>{' '}
                          <span
                            style={{
                              display: 'inline-block',
                              width: '1rem',
                              height: '1rem',
                              backgroundColor: formState.color.hex,
                              borderRadius: '50%',
                              marginRight: '0.5rem',
                              verticalAlign: 'middle',
                              border: '1px solid #ccc',
                            }}
                          />
                          <span>{formState.color.name}</span>
                        </li>
                      )}
                      {selectedShape && (
                        <li>
                          <strong>Shape:</strong> {selectedShape}
                        </li>
                      )}
                      {selectedLength && (
                        <li>
                          <strong>Length:</strong> {selectedLength}
                        </li>
                      )}
                      <li>
                        <strong>Fulfillment:</strong>{' '}
                        {formState.fulfillment === 'in_person' ? 'In-Person' : 'Shipping'}
                      </li>
                    </ul>
                  </div>
                )}

                <p className="mt-4 text-sm text-gray-600">
                  Have questions?{' '}
                  <Link to="/nails/faq" className="text-purple-600 underline hover:text-purple-800">
                    View our FAQs
                  </Link>
                </p>

                <button type="submit">Add to Cart</button>
              </form>
            </div>
          </div>

          <Footer />
        </>
      )}
    </>
  );
}
